import { OptionValue } from '../../option/option';
import { TypeaheadFilter } from '../typeahead';
import { cleanString } from './cleanString';

export const filterOptions = ({
  filterValue,
  parsedOptions,
  filter,
  whitespaceCharacters,
}: {
  filterValue: string;
  parsedOptions: OptionValue[];
  filter: TypeaheadFilter | 'equals';
  whitespaceCharacters?: string[];
}): {
  /** The options that match the filter value given the filter option */
  matches: OptionValue[];
  /** The options that do not match the filter value given the filter option. This is used for recursion */
  noMatches: OptionValue[];
} => {
  let matches = [];

  const cleanFilterValue = cleanString(filterValue, whitespaceCharacters);

  switch (filter) {
    case 'equals': {
      // Get all matches that are equal to the filterValue
      matches = parsedOptions.filter(parsedOption => cleanString(parsedOption.label) === cleanFilterValue);
      break;
    }
    case 'startsWith': {
      // First get all matches that are equal to the filterValue
      const { matches: prevMatches, noMatches } = filterOptions({ filterValue, parsedOptions, filter: 'equals', whitespaceCharacters });
      matches.push(...prevMatches);
      // Then get all matches that start with the filterValue
      const newMatches = noMatches.filter(option => cleanString(option.label).startsWith(cleanFilterValue));
      matches.push(...newMatches);
      break;
    }
    case 'contains': {
      // First get all matches that contain the entire filterValue
      const { matches: prevMatches, noMatches } = filterOptions({ filterValue, parsedOptions, filter: 'startsWith', whitespaceCharacters });
      matches.push(...prevMatches);
      // Then get all matches that contain the filterValue as a substring
      const newMatches = noMatches.filter(option => cleanString(option.label).includes(cleanFilterValue));
      matches.push(...newMatches);
      break;
    }
    case 'containsWords': {
      // First get all matches that contain the entire filterValue
      const { matches: prevMatches, noMatches } = filterOptions({ filterValue, parsedOptions, filter: 'contains', whitespaceCharacters });
      matches.push(...prevMatches);
      // Then get all matches that contain each word in the filterValue
      const words = cleanFilterValue.split(' ');
      for (let i = 0; i < noMatches.length; i += 1) {
        let match = true;
        let option = cleanString(noMatches[i].label);
        for (let j = 0; j < words.length; j += 1) {
          if (!option.includes(words[j])) {
            match = false;
            break;
          }
          // Remove the word from the option so that it doesn't match again
          option = option.replace(words[j], '');
        }
        if (match) {
          matches.push(noMatches[i]);
        }
      }
      break;
    }
    default: {
      throw new Error(`"${filter}" is not a valid option for the filter prop`);
    }
  }
  const noMatches = parsedOptions.filter(option => !matches.includes(option));
  return { matches, noMatches };
};
